<script>import * as m from '@app/paraglide/messages.js';import { projectState } from "@app/store/projectStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import sccLib from "@cclib/dict/608.js";
import { toast } from "@zerodevx/svelte-toast";
import { decode } from "html-entities";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import {
    getTotalChars,
    calculateCps,
    calculateWpm,
    calculateDuration,
} from "@cclib/lib/styleGuideTests.js";
import { 
    getEventMetrics, 
    getEventErrors, 
    hasEventErrors 
} from "@app/lib/eventValidation.js";
import { metricsState } from "@app/store/metricsStore.svelte.js";

let errorTypeFilter = $state("all"); //all, timing, content, review
function getErrorTypeLabel(type) {
    if (type === "timing") return m.error_nav_timing_label();
    if (type === "content") return m.error_nav_content_label();
    if (type === "review") return m.error_nav_review_label();
    return m.error_nav_all_label();
}
let selectedEvent = $derived(
    eventGroupState[projectState.selected]?.events[
        eventGroupState[projectState.selected]?.selected[0]
    ],
);
let plainText = $derived(convertToPlainText(selectedEvent?.text || ""));
let lineInfo = $derived.by(() => {
    let lines = [];
    plainText.split("\n").forEach((line) => {
        lines.push(decode(line).length);
    });

    return lines;
});

let totalWords = $derived(plainText.split(/\s/gm).length);

let totalChars = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return 0;
    // Use the imported getTotalChars function
    return getTotalChars(plainText, "text");
});

let cpsChars = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return 0;
    // Reading rate (CPS) can optionally exclude spaces based on user metrics settings
    return getTotalChars(plainText, "text", metricsState.cpsIncludeSpaces === false);
});

let duration = $derived(
    calculateDuration(selectedEvent.start, selectedEvent.end, 3),
);
let wpm = $derived(calculateWpm(totalWords, duration));
let cps = $derived(calculateCps(cpsChars, duration));
let isSearching = $state(false);

/**
 * Check if an event has errors based on the current filter
 * Uses the shared hasEventErrors utility with errorTypeFilter
 */
function checkEventHasErrors(event, eventGroup, eventIndex) {
    return hasEventErrors(event, eventGroup, eventIndex, errorTypeFilter);
}

let errorList = $derived.by(() => {
    try {
        let errors = [];
        if (!eventGroupState[projectState.selected]) {
            return errors;
        }

        if (wpm > eventGroupState[projectState.selected].maxWpm) {
            errors.push({
                type: "timing",
                msg: m.error_nav_wpm_exceeds({
                    maxWpm: eventGroupState[projectState.selected].maxWpm,
                }),
                hint: m.error_nav_wpm_exceeds_hint(),
            });
        }

        if (cps > eventGroupState[projectState.selected].maxCps) {
            errors.push({
                type: "timing",
                msg: m.error_nav_cps_exceeds({
                    maxCps: eventGroupState[projectState.selected].maxCps,
                }),
                hint: m.error_nav_cps_exceeds_hint(),
            });
        }

        if (
            duration < eventGroupState[projectState.selected].minDuration ||
            duration > eventGroupState[projectState.selected].maxDuration
        ) {
            errors.push({
                type: "timing",
                msg: m.error_nav_duration_out_of_bounds({
                    minDuration: eventGroupState[projectState.selected].minDuration,
                    maxDuration: eventGroupState[projectState.selected].maxDuration,
                }),
                hint: m.error_nav_duration_out_of_bounds_hint(),
            });
        }

        if (lineInfo) {
            if (
                lineInfo.some(
                    (el) =>
                        el > eventGroupState[projectState.selected].maxChars,
                ) ||
                lineInfo.length >
                    eventGroupState[projectState.selected].maxLines
            ) {
                errors.push({
                    type: "content",
                    msg: m.error_nav_text_content_exceeds({
                        maxChars: eventGroupState[projectState.selected].maxChars,
                        maxLines: eventGroupState[projectState.selected].maxLines,
                    }),
                    hint: m.error_nav_text_content_exceeds_hint(),
                });
            }
        }

        if (selectedEvent.approved === false) {
            errors.push({
                type: "review",
                msg: m.error_nav_event_disapproved(),
                hint: m.error_nav_event_disapproved_hint(),
            });
        }

        if (selectedEvent.notes && !selectedEvent.notesCheck) {
            errors.push({
                type: "review",
                msg: m.error_nav_notes_incomplete(),
                hint: m.error_nav_notes_incomplete_hint(),
            });
        }

        if (selectedEvent.reply && !selectedEvent.replyCheck) {
            errors.push({
                type: "review",
                msg: m.error_nav_reply_incomplete(),
                hint: m.error_nav_reply_incomplete_hint(),
            });
        }

        if (eventGroupState[projectState.selected].illegalChars && plainText) {
            let illegalChar = plainText.split("").find((char) => {
                return sccLib.valid.indexOf(char) === -1;
            });

            if (illegalChar) {
                errors.push({
                    type: "content",
                    msg: m.error_nav_illegal_characters({
                        character: illegalChar,
                    }),
                    hint: m.error_nav_illegal_characters_hint(),
                });
            }
        }

        if (eventGroupState[projectState.selected].overlap) {
            if (selectedEvent.start === false || selectedEvent.end === false) {
                return errors;
            }

            let eventIndex = eventGroupState[projectState.selected]?.selected[0];

            if ( eventIndex &&
                selectedEvent.start <
                    eventGroupState[projectState.selected]?.events[
                        eventIndex - 1
                    ]?.end ||
                selectedEvent.end >
                    eventGroupState[projectState.selected]?.events[
                        eventIndex + 1
                    ]?.start
            ) {
                errors.push({
                    type: "timing",
                    msg: m.error_nav_event_overlap(),
                    hint: m.error_nav_event_overlap_hint(),
                });
            }
        }

        return errors;
    } catch (err) {
        return [];
    }
});

async function findError(dir) {
    if (!eventGroupState[projectState.selected]) {
        toast.push(m.error_nav_no_event_group_toast(), {
            classes: ["toast-error"],
        });
        return;
    }

    isSearching = true;

    // Use setTimeout to allow the UI to update with the spinner
    setTimeout(() => {
        try {
            const eventGroup = eventGroupState[projectState.selected];
            const events = eventGroup.events;
            const totalEvents = events.length;

            if (totalEvents === 0) {
                toast.push(m.error_nav_no_events_toast(), {
                    classes: ["toast-error"],
                });
                isSearching = false;
                return;
            }

            // Determine starting index
            let startIndex = 0;
            if (eventGroup.selected && eventGroup.selected.length > 0) {
                startIndex = eventGroup.selected[0];
            }

            let foundIndex = -1;

            if (dir === "next") {
                // Search forward from the next event
                for (let i = startIndex + 1; i < totalEvents; i++) {
                    if (checkEventHasErrors(events[i], eventGroup, i)) {
                        foundIndex = i;
                        break;
                    }
                }
                // Wrap around to beginning if not found
                if (foundIndex === -1) {
                    for (let i = 0; i <= startIndex; i++) {
                        if (checkEventHasErrors(events[i], eventGroup, i)) {
                            foundIndex = i;
                            break;
                        }
                    }
                }
            } else if (dir === "prev") {
                // Search backward from the previous event
                for (let i = startIndex - 1; i >= 0; i--) {
                    if (checkEventHasErrors(events[i], eventGroup, i)) {
                        foundIndex = i;
                        break;
                    }
                }
                // Wrap around to end if not found
                if (foundIndex === -1) {
                    for (let i = totalEvents - 1; i >= startIndex; i--) {
                        if (checkEventHasErrors(events[i], eventGroup, i)) {
                            foundIndex = i;
                            break;
                        }
                    }
                }
            }

            if (foundIndex !== -1) {
                // Update selection to the found event
                eventGroup.selected = [foundIndex];

                window.dispatchEvent(
                    new CustomEvent("scrollToVirtualListItem", {
                        detail: { index: foundIndex },
                    }),
                );

                if (lockState.video && player) {
                    player.currentTime =
                        eventGroupState[projectState.selected].events[
                            foundIndex
                        ].start;
                }

                toast.push(m.error_nav_event_selected_toast({eventNum: foundIndex + 1}), {
                    classes: ["toast-success"],
                });
            } else {
                const filterText =
                    errorTypeFilter === "all" ? "" : ` ${errorTypeFilter}`;
                toast.push(m.error_nav_no_errors_found_toast({filter: filterText}), {
                    classes: ["toast-info"],
                });
            }
        } catch (err) {
            console.error("Error finding error:", err);
            toast.push(m.error_nav_search_error_toast(), {
                classes: ["toast-error"],
            });
        } finally {
            isSearching = false;
        }
    }, 50);
}
</script>

<!-- Error summary header -->
<div
    class="d-flex justify-content-between align-items-center p-3 bg-light border-bottom">
    <div>
        <!-- Error type filter -->
        <select
            id="ErrorTypeFilterSelect"
            class="form-select form-select-sm"
            bind:value={errorTypeFilter}>
            <option value="all">{m.error_nav_all_errors_option()}</option>
            <option value="timing">{m.error_nav_timing_errors_option()}</option>
            <option value="content">{m.error_nav_content_errors_option()}</option>
            <option value="review">{m.error_nav_review_errors_option()}</option>
        </select>
    </div>
    <div class="btn-group">
        <button
            type="button"
            class="btn btn-secondary btn-sm"
            onclick={() => findError("prev")}
            id="prevErrorButton"
            disabled={isSearching}
            aria-label={m.error_nav_prev_error_aria_label()}>
            {#if isSearching}
                <span
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"></span>
            {:else}
                <i class="bi bi-chevron-left"></i>
            {/if}
            {m.error_nav_previous_button()}
        </button>
        <button
            type="button"
            class="btn btn-secondary btn-sm"
            onclick={() => findError("next")}
            id="nextErrorButton"
            disabled={isSearching}
            aria-label={m.error_nav_next_error_aria_label()}>
            {#if isSearching}
                <span
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"></span>
            {:else}
                {m.error_nav_next_button()}
            {/if}
            {#if !isSearching}
                <i class="bi bi-chevron-right"></i>
            {/if}
        </button>
    </div>
</div>

<!-- Error details -->
{#if selectedEvent}
    <div id="errorList" class="p-3">
        {#if errorList.length === 0}
            <div class="alert alert-success mb-0" role="alert">
                <i class="bi bi-check-circle-fill me-2"></i>
                <strong>{m.error_nav_no_errors_found()}</strong>
            </div>
        {:else}
            <!-- Filter and display errors -->
            {#each errorList.filter((error) => errorTypeFilter === "all" || error.type === errorTypeFilter) as error, index}
                <div class="card mb-2">
                    <div class="card-body p-3">
                        <div class="d-flex align-items-start">
                            <!-- Error icon based on type -->
                            <div class="me-3">
                                {#if error.type === "timing"}
                                    <i
                                        class="bi bi-clock-fill text-warning fs-4"
                                    ></i>
                                {:else if error.type === "content"}
                                    <i
                                        class="bi bi-exclamation-triangle-fill text-danger fs-4"
                                    ></i>
                                {:else if error.type === "review"}
                                    <i class="bi bi-flag-fill text-info fs-4"
                                    ></i>
                                {/if}
                            </div>

                            <!-- Error content -->
                            <div class="flex-grow-1">
                                <h6 class="card-title mb-1">
                                    {#if error.type === "timing"}
                                        <span class="badge bg-warning text-dark"
                                            >{m.error_nav_timing_label()}</span>
                                    {:else if error.type === "content"}
                                        <span class="badge bg-danger"
                                            >{m.error_nav_content_label()}</span>
                                    {:else if error.type === "review"}
                                        <span class="badge bg-info"
                                            >{m.error_nav_review_label()}</span>
                                    {/if}
                                </h6>
                                <p class="card-text mb-2">
                                    <strong>{error.msg}</strong>
                                </p>
                                {#if error.hint}
                                    <p class="card-text mb-0">
                                        <small class="text-muted">
                                            <i class="bi bi-lightbulb me-1"></i>
                                            {error.hint}
                                        </small>
                                    </p>
                                {/if}
                            </div>
                        </div>
                    </div>
                </div>
            {:else}
                <div class="alert alert-secondary mb-0" role="alert">
                    <i class="bi bi-filter-circle me-2"></i>
                    {#if errorTypeFilter === "all"}
                        {m.error_nav_no_errors_for_event()}
                    {:else}
                        {m.error_nav_no_filtered_errors({
                            errorType: getErrorTypeLabel(errorTypeFilter),
                        })}
                    {/if}
                </div>
            {/each}
        {/if}
    </div>
{:else}
    <div class="text-center text-muted py-4">
        <i class="bi bi-info-circle me-2"></i>
        <small>{m.error_nav_no_event_selected()}</small>
    </div>
{/if}

<style>
#errorList {
    height: 10vh;
    resize: vertical;
    overflow-y: auto;
}
</style>
